import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useIsAdmin } from "../hooks/useFeatureFlags";
import { useAuth } from "../components/auth/AuthContext";
import NavBar from "../components/layout/NavBar";
import { HeaderBar } from "../components/layout/TopBar";
import { Link, useNavigate } from "react-router-dom";
import { getDatabases, getDatabaseDocumentCounts } from "../api/adminApi";
import { useLogs } from "../components/logs/LogsContext";
import { getMyNotifications } from "../api/notificationsApi";
import { useUpdateStatus } from "../hooks/useUpdateStatus";
import { useLicense } from "../licensing/LicenseContext";

/* ── Icon helper ─────────────────────────────────────────── */
const Icon = ({ name, size = 28, className = "" }) => {
  const p = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.5",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    className,
  };

  const paths = {
    database: (
      <>
        <ellipse cx="12" cy="5" rx="9" ry="3" />
        <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
        <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
      </>
    ),
    documents: (
      <>
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
        <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
      </>
    ),
    bell: (
      <>
        <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 01-3.46 0" />
      </>
    ),
    shield: (
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    ),
    grid: (
      <>
        <rect x="3" y="3" width="7" height="7" />
        <rect x="14" y="3" width="7" height="7" />
        <rect x="3" y="14" width="7" height="7" />
        <rect x="14" y="14" width="7" height="7" />
      </>
    ),
    layers: (
      <>
        <path d="M12 2L2 7l10 5 10-5-10-5z" />
        <path d="M2 17l10 5 10-5" />
        <path d="M2 12l10 5 10-5" />
      </>
    ),
    monitor: (
      <>
        <rect x="2" y="7" width="20" height="14" rx="2" />
        <path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16" />
        <circle cx="9" cy="12" r="1" />
        <circle cx="15" cy="12" r="1" />
      </>
    ),
    server: (
      <>
        <rect x="2" y="2" width="20" height="8" rx="2" />
        <rect x="2" y="14" width="20" height="8" rx="2" />
        <circle cx="6" cy="6" r="1" fill="currentColor" />
        <circle cx="6" cy="18" r="1" fill="currentColor" />
      </>
    ),
    cpu: (
      <>
        <rect x="4" y="4" width="16" height="16" rx="2" />
        <circle cx="12" cy="12" r="3" />
        <path d="M12 1v3M12 20v3M1 12h3M20 12h3" />
      </>
    ),
    // Hub and spokes: one installation, several machines.
    network: (
      <>
        <rect x="9" y="2" width="6" height="5" rx="1" />
        <rect x="2" y="17" width="6" height="5" rx="1" />
        <rect x="16" y="17" width="6" height="5" rx="1" />
        <path d="M12 7v5M5 17v-3h14v3" />
      </>
    ),
    storage: (
      <>
        <ellipse cx="12" cy="5" rx="9" ry="3" />
        <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
        <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
      </>
    ),
    wrench: (
      <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" />
    ),
    users: (
      <>
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
      </>
    ),
    filelog: (
      <>
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
        <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
      </>
    ),
    upload: (
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />
    ),
    archive: (
      <>
        <rect x="2" y="3" width="20" height="5" rx="1" />
        <path d="M4 8v11a2 2 0 002 2h12a2 2 0 002-2V8" />
        <path d="M10 12h4" />
      </>
    ),
    list: (
      <>
        <line x1="8" y1="6" x2="21" y2="6" />
        <line x1="8" y1="12" x2="21" y2="12" />
        <line x1="8" y1="18" x2="21" y2="18" />
        <line x1="3" y1="6" x2="3.01" y2="6" />
        <line x1="3" y1="12" x2="3.01" y2="12" />
        <line x1="3" y1="18" x2="3.01" y2="18" />
      </>
    ),
    plus: (
      <>
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
      </>
    ),
    workflow: (
      <>
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </>
    ),
    table: (
      <>
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <path d="M3 9h18M3 15h18M9 3v18" />
      </>
    ),
    trendUp: (
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
    ),
    download: (
      <>
        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
      </>
    ),
    checkCircle: (
      <>
        <circle cx="12" cy="12" r="10" />
        <polyline points="9 12 11 14 15 10" />
      </>
    ),
    arrowRight: (
      <>
        <line x1="5" y1="12" x2="19" y2="12" />
        <polyline points="12 5 19 12 12 19" />
      </>
    ),
    lock: (
      <>
        <rect x="3" y="11" width="18" height="11" rx="2" />
        <path d="M7 11V7a5 5 0 0110 0v4" />
      </>
    ),
    recycle: (
      <>
        <polyline points="23 4 23 10 17 10" />
        <polyline points="1 20 1 14 7 14" />
        <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
      </>
    ),
    tag: (
      <>
        <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" />
        <line x1="7" y1="7" x2="7.01" y2="7" />
      </>
    ),
    folder: (
      <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" />
    ),
    history: (
      <>
        <path d="M3 3v5h5" />
        <path d="M3.05 13A9 9 0 106 5.3L3 8" />
        <path d="M12 7v5l4 2" />
      </>
    ),
  };

  return <svg {...p}>{paths[name] || null}</svg>;
};

/* ── Decorative sparkline ────────────────────────────────── */
const Sparkline = ({ color = "#4f46e5", values = [5, 7, 6, 9, 8, 11, 10, 13, 12, 15] }) => {
  const w = 88, h = 28;
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;
  const pts = values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * w;
      const y = h - 4 - ((v - min) / range) * (h - 8);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  const fillPts = `0,${h} ${pts} ${w},${h}`;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} fill="none" style={{ display: "block" }}>
      <defs>
        <linearGradient id={`sg-${color.replace("#", "")}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.18" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={fillPts} fill={`url(#sg-${color.replace("#", "")})`} />
      <polyline points={pts} stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

/* ── Hero illustration ───────────────────────────────────── */
const HeroIllustration = () => (
  <svg width="220" height="148" viewBox="0 0 220 148" fill="none" aria-hidden="true">
    {/* Back platform */}
    <ellipse cx="130" cy="116" rx="60" ry="14" fill="rgba(255,255,255,0.08)" />
    {/* Cylinder 1 – back */}
    <ellipse cx="148" cy="74" rx="36" ry="11" fill="rgba(255,255,255,0.14)" />
    <rect x="112" y="74" width="72" height="40" fill="rgba(255,255,255,0.10)" />
    <ellipse cx="148" cy="114" rx="36" ry="11" fill="rgba(255,255,255,0.13)" />
    {/* Cylinder 2 – mid */}
    <ellipse cx="124" cy="58" rx="28" ry="9" fill="rgba(255,255,255,0.20)" />
    <rect x="96" y="58" width="56" height="32" fill="rgba(255,255,255,0.15)" />
    <ellipse cx="124" cy="90" rx="28" ry="9" fill="rgba(255,255,255,0.18)" />
    {/* Cylinder 3 – front */}
    <ellipse cx="104" cy="44" rx="20" ry="7" fill="rgba(255,255,255,0.30)" />
    <rect x="84" y="44" width="40" height="24" fill="rgba(255,255,255,0.22)" />
    <ellipse cx="104" cy="68" rx="20" ry="7" fill="rgba(255,255,255,0.27)" />
    {/* Shield badge */}
    <path d="M185 18 L202 24 L202 37 C202 47 185 53 185 53 C185 53 168 47 168 37 L168 24 Z"
      fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.35)" strokeWidth="1.2" />
    <polyline points="178,35 182,39 192,29"
      stroke="rgba(255,255,255,0.85)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    {/* Floating accent dots */}
    <circle cx="60" cy="18" r="5" fill="rgba(255,255,255,0.25)" />
    <circle cx="48" cy="52" r="3.5" fill="rgba(255,255,255,0.18)" />
    <circle cx="200" cy="88" r="4" fill="rgba(255,255,255,0.20)" />
    <circle cx="212" cy="56" r="2.5" fill="rgba(255,255,255,0.22)" />
    <circle cx="72" cy="96" r="3" fill="rgba(255,255,255,0.15)" />
    {/* Connection lines */}
    <line x1="60" y1="18" x2="84" y2="44" stroke="rgba(255,255,255,0.12)" strokeWidth="1" strokeDasharray="3 3" />
    <line x1="200" y1="88" x2="184" y2="74" stroke="rgba(255,255,255,0.12)" strokeWidth="1" strokeDasharray="3 3" />
  </svg>
);

/* ── Section card ────────────────────────────────────────── */
const SectionCard = ({ to, icon, title, subtitle, accent = "#4f46e5" }) => {
  // Build explicit tint: append 18 (hex ≈ 9% opacity) for the icon bg
  const iconBg = accent + "18";
  return (
    <Link to={to} className="adm-section-card" style={{ "--card-accent": accent }}>
      <div className="adm-section-card__icon-wrap" style={{ background: iconBg, color: accent }}>
        <Icon name={icon} size={22} />
      </div>
      <div className="adm-section-card__text">
        <div className="adm-section-card__title">{title}</div>
        <div className="adm-section-card__subtitle">{subtitle}</div>
      </div>
      <div className="adm-section-card__arrow">
        <Icon name="arrowRight" size={14} />
      </div>
    </Link>
  );
};

/* ── Main Component ──────────────────────────────────────── */
export default function AdminHub() {
  const { t } = useTranslation();
  const isAdmin = useIsAdmin();
  const { token, user } = useAuth();
  const { logs } = useLogs();
  const { hasFeature } = useLicense();
  const navigate = useNavigate();

  const [databases, setDatabases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [docStats, setDocStats] = useState({ totalDocuments: 0, databases: [] });
  const [docLoading, setDocLoading] = useState(false);
  const [docDrill, setDocDrill] = useState(false);
  const drillRef = useRef(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  /* ── DOCUMENTS drill carousel: keep the widget at KPI size and page through
        the per-database cards horizontally with the ‹ › controls. ── */
  const updateDrillNav = useCallback(() => {
    const el = drillRef.current;
    if (!el) return;
    setCanScrollPrev(el.scrollLeft > 2);
    setCanScrollNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }, []);

  const scrollDrill = useCallback((dir) => {
    const el = drillRef.current;
    if (!el) return;
    const item = el.querySelector(".adm-kpi-drill__item");
    const step = item ? item.offsetWidth + 8 : 120;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (!docDrill) return;
    // Wait for the strip to render before measuring overflow.
    const id = setTimeout(updateDrillNav, 0);
    return () => clearTimeout(id);
  }, [docDrill, docStats, updateDrillNav]);
  const [notificationSummary, setNotificationSummary] = useState({ total: 0, unread: 0 });
  const [currentTime, setCurrentTime] = useState(new Date());
  const { status: updateStatus } = useUpdateStatus();

  /* ── Live clock ── */
  useEffect(() => {
    const id = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const timeStr = currentTime.toLocaleTimeString("en-US", {
    hour: "2-digit", minute: "2-digit", second: "2-digit",
    timeZone: "UTC", hour12: false,
  }) + " UTC";

  /* ── Data fetching ── */
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const dbs = await getDatabases(token);
        if (!cancelled) setDatabases(Array.isArray(dbs) ? dbs : dbs || []);
      } catch { /* silent */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [token]);

  /* ── Per-database document counts (drives the DOCUMENTS widget) ── */
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      setDocLoading(true);
      try {
        const data = await getDatabaseDocumentCounts(token);
        if (!cancelled && data) {
          setDocStats({
            totalDocuments: Number(data.totalDocuments) || 0,
            databases: Array.isArray(data.databases) ? data.databases : [],
          });
        }
      } catch { /* silent */ }
      finally { if (!cancelled) setDocLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getMyNotifications(token);
        if (!cancelled && Array.isArray(data)) {
          const total = data.length;
          const unread = data.filter((n) => !n.isRead && !n.dismissed).length;
          setNotificationSummary({ total, unread });
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [token]);

  /* ── Derived metrics ──
     Document totals are NOT derived here: GetDatabases carries no per-DB count, so
     the old client-side sum always resolved to 0. The DOCUMENTS widget is now driven
     by docStats (GetDatabaseDocumentCounts). This memo covers the other KPI tiles. */
  const metrics = useMemo(() => {
    if (!databases || databases.length === 0)
      return { totalDatabases: 0, healthBuckets: { healthy: 0, degraded: 0, offline: 0 }, recentCount: 0 };

    const totalDatabases = databases.length;

    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const recentCount = databases.filter((db) => {
      const created = db.createdAt || db.CreatedAt || db.created;
      return created && new Date(created) >= weekAgo;
    }).length;

    const healthBuckets = databases.reduce(
      (acc, db) => {
        const h = (db.health || "").toLowerCase();
        if (h === "healthy") acc.healthy += 1;
        else if (h === "degraded" || h === "warning") acc.degraded += 1;
        else if (h === "offline" || h === "error") acc.offline += 1;
        else acc.healthy += 1;
        return acc;
      },
      { healthy: 0, degraded: 0, offline: 0 },
    );

    return { totalDatabases, healthBuckets, recentCount };
  }, [databases]);

  const totalIssues = metrics.healthBuckets.degraded + metrics.healthBuckets.offline;
  const systemHealthy = totalIssues === 0;
  const displayName = user?.fullName || user?.name || user?.userName || user?.email?.split("@")[0] || "Admin";

  return (
    <div className="app-root">
      <div className="nav-strip">
        <NavBar />
      </div>
      <HeaderBar />

      <div className="page-container adm">

        {/* ── Hero Banner ── */}
        <div className="adm-hero">
          <div className="adm-hero__content">
            <div className="adm-hero__eyebrow">
              <span className={`adm-hero__badge adm-hero__badge--${systemHealthy ? "ok" : "warn"}`}>
                <span className="adm-hero__badge-dot" />
                {systemHealthy
                  ? t("admin.systemHealthy", { defaultValue: "System Healthy" })
                  : t("admin.systemIssues", { defaultValue: "Issues Detected" })}
              </span>
            </div>
            <h1 className="adm-hero__title">
              {t("admin.welcomeBackUser", { name: displayName, defaultValue: `Welcome back, ${displayName}!` })} 👋
            </h1>
            <p className="adm-hero__subtitle">
              {t("admin.heroSubtitle", {
                defaultValue: "Your DIXI Vault system is running smoothly. Here's what's happening today.",
              })}
            </p>
            <div className="adm-hero__meta">
              <span className="adm-hero__time">{timeStr}</span>
            </div>
            <div className="adm-hero__actions">
              <button className="adm-hero__btn adm-hero__btn--primary" onClick={() => navigate("/admin/indexes")}>
                <Icon name="plus" size={14} />
                {t("admin.newDatabase", { defaultValue: "New Database" })}
              </button>
              <button className="adm-hero__btn adm-hero__btn--ghost" onClick={() => navigate("/upload")}>
                <Icon name="upload" size={14} />
                {t("admin.uploadDocuments", { defaultValue: "Upload Documents" })}
              </button>
              <button className="adm-hero__btn adm-hero__btn--ghost" onClick={() => navigate("/flow-designer")}>
                <Icon name="workflow" size={14} />
                {t("admin.createWorkflow", { defaultValue: "Create Workflow" })}
              </button>
            </div>
          </div>
          <div className="adm-hero__illustration" aria-hidden="true">
            <HeroIllustration />
          </div>
        </div>

        {/* ── System Overview ── */}
        <section className="adm-section">
          <div className="adm-section__head">
            <h2 className="adm-section__heading">
              {t("admin.systemOverview", { defaultValue: "System Overview" })}
            </h2>
            <Link to="/admin/workers" className="adm-section__view-all">
              {t("admin.viewSystemStatus", { defaultValue: "View System Status" })} →
            </Link>
          </div>
          <div className="adm-overview-grid">

            {/* Databases */}
            <div className="adm-kpi-card">
              <div className="adm-kpi-card__top">
                <div>
                  <span className="adm-kpi-card__label">
                    {t("admin.database", { defaultValue: "DATABASES" })}
                  </span>
                  <div className="adm-kpi-card__value">
                    {loading ? <span className="adm-kpi-card__skeleton" /> : metrics.totalDatabases}
                  </div>
                </div>
                <div className="adm-kpi-card__icon adm-kpi-card__icon--blue">
                  <Icon name="database" size={20} />
                </div>
              </div>
              <div className="adm-kpi-card__spark">
                <Sparkline color="#3b82f6" values={[3, 4, 4, 5, 5, 6, 7, 8, 9, metrics.totalDatabases || 10]} />
              </div>
              <div className="adm-kpi-card__footer">
                {metrics.recentCount > 0 ? (
                  <span className="adm-kpi-card__trend adm-kpi-card__trend--up">
                    +{metrics.recentCount} {t("admin.thisWeek", { defaultValue: "this week" })}
                  </span>
                ) : (
                  <span className="adm-kpi-card__trend adm-kpi-card__trend--neutral">
                    {t("admin.noNewThisWeek", { defaultValue: "No new this week" })}
                  </span>
                )}
                <Link to="/admin/indexes" className="adm-kpi-card__link">
                  {t("labels.manage", { defaultValue: "Manage" })} →
                </Link>
              </div>
            </div>

            {/* Documents — drillable: total ⇄ per-database (horizontally scrollable) */}
            <div className={`adm-kpi-card adm-kpi-card--drillable${docDrill ? " adm-kpi-card--drilled" : ""}`}>
              <div className="adm-kpi-card__top">
                <div>
                  <span className="adm-kpi-card__label">
                    {docDrill
                      ? t("admin.kpi.documentsByBase", { defaultValue: "DOCUMENTS BY DATABASE" })
                      : t("admin.kpi.documents", { defaultValue: "DOCUMENTS" })}
                  </span>
                  {!docDrill && (
                    <div className="adm-kpi-card__value">
                      {docLoading ? <span className="adm-kpi-card__skeleton" /> : docStats.totalDocuments.toLocaleString()}
                    </div>
                  )}
                </div>
                <div className="adm-kpi-card__icon adm-kpi-card__icon--teal">
                  <Icon name="documents" size={20} />
                </div>
              </div>

              {docDrill ? (
                docStats.databases.length === 0 ? (
                  <div className="adm-kpi-drill__empty">
                    {docLoading
                      ? t("admin.loading", { defaultValue: "Loading…" })
                      : t("admin.kpi.noBases", { defaultValue: "No databases yet" })}
                  </div>
                ) : (
                  <div className="adm-kpi-carousel">
                    <button
                      type="button"
                      className="adm-kpi-carousel__nav"
                      onClick={() => scrollDrill(-1)}
                      disabled={!canScrollPrev}
                      aria-label={t("labels.previous", { defaultValue: "Previous" })}
                    >
                      ‹
                    </button>
                    <div
                      className="adm-kpi-drill"
                      ref={drillRef}
                      onScroll={updateDrillNav}
                      role="list"
                      aria-label={t("admin.kpi.documentsByBase", { defaultValue: "Documents by database" })}
                    >
                      {docStats.databases.map((db) => (
                        <div className="adm-kpi-drill__item" role="listitem" key={db.databaseId}>
                          <span className="adm-kpi-drill__name" title={db.displayName || db.name}>
                            {db.displayName || db.name}
                          </span>
                          <span className="adm-kpi-drill__count">
                            {Number(db.documentCount || 0).toLocaleString()}
                          </span>
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      className="adm-kpi-carousel__nav"
                      onClick={() => scrollDrill(1)}
                      disabled={!canScrollNext}
                      aria-label={t("labels.next", { defaultValue: "Next" })}
                    >
                      ›
                    </button>
                  </div>
                )
              ) : (
                <button
                  type="button"
                  className="adm-kpi-card__spark adm-kpi-card__spark--clickable"
                  onClick={() => setDocDrill(true)}
                  disabled={docLoading}
                  title={t("admin.kpi.viewByDatabase", { defaultValue: "View by database" })}
                  aria-label={t("admin.kpi.viewByDatabase", { defaultValue: "View by database" })}
                >
                  <Sparkline color="#14b8a6" values={[20, 35, 28, 50, 42, 60, 55, 70, 65, 80]} />
                </button>
              )}

              <div className="adm-kpi-card__footer">
                {docDrill ? (
                  <button
                    type="button"
                    className="adm-kpi-card__drill-toggle"
                    onClick={() => setDocDrill(false)}
                  >
                    ← {t("admin.kpi.backToTotal", { defaultValue: "Back to total" })}
                  </button>
                ) : (
                  <>
                    <span className="adm-kpi-card__trend adm-kpi-card__trend--neutral">
                      {docStats.totalDocuments === 0
                        ? t("admin.noUploads7Days", { defaultValue: "No uploads in last 7 days" })
                        : t("admin.kpi.clickChartHint", { defaultValue: "Click chart to view by database" })}
                    </span>
                    <Link to="/upload" className="adm-kpi-card__link">
                      {t("admin.uploadDocuments", { defaultValue: "Upload" })} →
                    </Link>
                  </>
                )}
              </div>
            </div>

            {/* Notifications */}
            <div className={`adm-kpi-card${notificationSummary.unread > 0 ? " adm-kpi-card--warn" : ""}`}>
              <div className="adm-kpi-card__top">
                <div>
                  <span className="adm-kpi-card__label">
                    {t("user.notifications", { defaultValue: "NOTIFICATIONS" })}
                  </span>
                  <div className="adm-kpi-card__value">
                    {notificationSummary.unread}
                    <span className="adm-kpi-card__value-suffix">
                      {t("labels.unread", { defaultValue: "Unread" })}
                    </span>
                  </div>
                </div>
                <div className="adm-kpi-card__icon adm-kpi-card__icon--amber">
                  <Icon name="bell" size={20} />
                </div>
              </div>
              <div className="adm-kpi-card__spark">
                <Sparkline color="#f59e0b" values={[1, 2, 1, 3, 2, 4, 3, 5, 4, notificationSummary.unread || 5]} />
              </div>
              <div className="adm-kpi-card__footer">
                <div className="adm-kpi-card__meta-row">
                  <span className="adm-kpi-card__dot adm-kpi-card__dot--amber" />
                  {t("admin.notifications.unread", { defaultValue: "Unread" })}: {notificationSummary.unread}
                  <span className="adm-kpi-card__dot adm-kpi-card__dot--gray" style={{ marginLeft: 10 }} />
                  {t("admin.notifications.read", { defaultValue: "Read" })}: {notificationSummary.total - notificationSummary.unread}
                </div>
              </div>
            </div>

            {/* Service Health */}
            <div className="adm-kpi-card">
              <div className="adm-kpi-card__top">
                <div>
                  <span className="adm-kpi-card__label">
                    {t("admin.serviceHealth", { defaultValue: "SERVICE HEALTH" })}
                  </span>
                  <div className="adm-kpi-card__value">
                    {totalIssues}
                    <span className="adm-kpi-card__value-suffix">
                      {t("admin.issues", { defaultValue: "Issues" })}
                    </span>
                  </div>
                </div>
                <div className="adm-kpi-card__icon adm-kpi-card__icon--green">
                  <Icon name="shield" size={20} />
                </div>
              </div>
              <div className="adm-kpi-card__spark">
                <Sparkline color="#22c55e" values={[2, 1, 2, 0, 1, 0, 0, 1, 0, totalIssues]} />
              </div>
              <div className="adm-kpi-card__footer">
                <div className="adm-kpi-card__meta-row">
                  <span className="adm-kpi-card__dot adm-kpi-card__dot--green" />
                  {t("admin.health.healthy", { defaultValue: "Healthy" })}: {metrics.healthBuckets.healthy}
                  <span className="adm-kpi-card__dot adm-kpi-card__dot--amber" style={{ marginLeft: 8 }} />
                  {metrics.healthBuckets.degraded}
                  <span className="adm-kpi-card__dot adm-kpi-card__dot--red" style={{ marginLeft: 8 }} />
                  {metrics.healthBuckets.offline}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Operations ── */}
        <section className="adm-section">
          <div className="adm-section__head">
            <h2 className="adm-section__heading">
              {t("admin.operations", { defaultValue: "Operations" })}
            </h2>
          </div>
          <div className="adm-cards-grid">
            <SectionCard to="/admin/indexes" icon="grid" accent="#4f46e5"
              title={t("admin.indexes", { defaultValue: "Document Bases" })}
              subtitle={t("admin.indexesSubtitle", { defaultValue: "Manage document sources" })} />
            <SectionCard to="/admin/batches" icon="layers" accent="#0ea5e9"
              title={t("admin.batches", { defaultValue: "Ingestion Batches" })}
              subtitle={t("admin.batchesSubtitle", { defaultValue: "Monitor and manage processing" })} />
            <SectionCard to="/admin/workers" icon="monitor" accent="#8b5cf6"
              title={t("admin.workers", { defaultValue: "Worker Monitoring" })}
              subtitle={t("admin.workersSubtitle", { defaultValue: "Track background jobs" })} />
            <SectionCard to="/admin/db-config" icon="server" accent="#06b6d4"
              title={t("admin.dbConfig", { defaultValue: "Database Provider" })}
              subtitle={t("admin.dbConfigSubtitle", { defaultValue: "View connections" })} />
          </div>
        </section>

        {/* ── Content Governance ── */}
        <section className="adm-section">
          <div className="adm-section__head">
            <h2 className="adm-section__heading">
              {t("admin.contentGovernance", { defaultValue: "Content Governance" })}
            </h2>
          </div>
          <div className="adm-cards-grid">
            {hasFeature("content-governance") && (
              <SectionCard to="/admin/legal-holds" icon="lock" accent="#d97706"
                title={t("holds.title", { defaultValue: "Legal Holds" })}
                subtitle={t("admin.holdsSubtitle", { defaultValue: "Preserve documents under legal hold" })} />
            )}
            {hasFeature("content-governance") && (
              <SectionCard to="/admin/disposition" icon="recycle" accent="#059669"
                title={t("disposition.title", { defaultValue: "Defensible Disposition" })}
                subtitle={t("admin.dispositionSubtitle", { defaultValue: "Review & approve defensible deletion" })} />
            )}
            {hasFeature("content-governance") && (
              <SectionCard to="/admin/classification" icon="tag" accent="#6366f1"
                title={t("classification.title", { defaultValue: "Content Classification" })}
                subtitle={t("admin.classificationSubtitle", { defaultValue: "Label content for retention" })} />
            )}
            {hasFeature("ai-retrieval") && (
              <SectionCard to="/admin/enrichment" icon="search" accent="#7c3aed"
                title={t("enrichment.title", { defaultValue: "Document Enrichment" })}
                subtitle={t("enrichment.adminSubtitle", { defaultValue: "Choose which bases are understood" })} />
            )}
            {hasFeature("content-governance") && (
              <SectionCard to="/admin/content-organization" icon="folder" accent="#0d9488"
                title={t("contentOrg.title", { defaultValue: "Content Organization" })}
                subtitle={t("admin.contentOrgSubtitle", { defaultValue: "Taxonomy, content types & folders" })} />
            )}
            {hasFeature("content-governance") && (
              <SectionCard to="/admin/records-management" icon="archive" accent="#0f766e"
                title={t("recordsManagement.title", { defaultValue: "Records Management" })}
                subtitle={t("recordsManagement.hubSubtitle", { defaultValue: "File plan, retention schedules & declaration" })} />
            )}
            {hasFeature("content-governance") && (
              <SectionCard to="/admin/workflow" icon="workflow" accent="#4f46e5"
                title={t("workflow.title", { defaultValue: "Workflow" })}
                subtitle={t("workflow.hubSubtitle", { defaultValue: "Review & approval routing" })} />
            )}
            {hasFeature("content-governance") && (
              <SectionCard to="/admin/ediscovery" icon="search" accent="#4f46e5"
                title={t("eDiscovery.title", { defaultValue: "eDiscovery" })}
                subtitle={t("eDiscovery.hubSubtitle", { defaultValue: "Matters, review sets & certified productions" })} />
            )}
            {hasFeature("delivery-portal") && (
              <SectionCard to="/admin/delivery" icon="download" accent="#0891b2"
                title={t("delivery.title", { defaultValue: "Delivery & self-service portal" })}
                subtitle={t("delivery.hubSubtitle", { defaultValue: "Revocable links & external recipients" })} />
            )}
            {hasFeature("content-governance") && (
              <SectionCard to="/admin/access-control" icon="shield" accent="#7c3aed"
                title={t("accessControl.title", { defaultValue: "Access Control" })}
                subtitle={t("accessControl.hubSubtitle", { defaultValue: "Govern who can access what" })} />
            )}
            {hasFeature("content-governance") && (
              <SectionCard to="/admin/access-review" icon="filelog" accent="#a21caf"
                title={t("accessReview.title", { defaultValue: "Access Reviews" })}
                subtitle={t("accessReview.hubSubtitle", { defaultValue: "Recertify who has access" })} />
            )}
            {hasFeature("content-governance") && (
              <SectionCard to="/admin/content-access-logs" icon="filelog" accent="#6366f1"
                title={t("logs.contentTitle", { defaultValue: "Content Access" })}
                subtitle={t("logs.contentSubtitle", { defaultValue: "Track document access" })} />
            )}
            <SectionCard to="/admin/lifecycle" icon="archive" accent="#0ea5a4"
              title={t("admin.lifecycleMgmt", { defaultValue: "Lifecycle Management" })}
              subtitle={t("admin.lifecycleMgmtSubtitle", { defaultValue: "Retention rules, history & revert" })} />
            <SectionCard to="/admin/lifecycle-events" icon="history" accent="#8b5cf6"
              title={t("lifecycle.viewEvents", { defaultValue: "View events & history" })}
              subtitle={t("admin.lifecycleEventsSubtitle", { defaultValue: "Track, search & revert" })} />
          </div>
        </section>

        {/* ── Configuration ── */}
        <section className="adm-section">
          <div className="adm-section__head">
            <h2 className="adm-section__heading">
              {t("admin.configuration", { defaultValue: "Configuration" })}
            </h2>
          </div>
          <div className="adm-cards-grid">
            <SectionCard to="/admin/hosts" icon="cpu" accent="#f59e0b"
              title={t("admin.hosts", { defaultValue: "Host Management" })}
              subtitle={t("admin.hostsSubtitle", { defaultValue: "Check system status" })} />
            <SectionCard to="/admin/topology" icon="network" accent="#0ea5a4"
              title={t("fleetTopology.title", { defaultValue: "Fleet Topology" })}
              subtitle={t("fleetTopology.hubSubtitle", { defaultValue: "Every machine and what runs on it" })} />
            <SectionCard to="/admin/storage-config" icon="storage" accent="#10b981"
              title={t("admin.storageConfig", { defaultValue: "Storage Configuration" })}
              subtitle={t("admin.storageConfigSubtitle", { defaultValue: "Manage settings & capacity" })} />
            <SectionCard to="/admin/processing-config" icon="wrench" accent="#ef4444"
              title={t("admin.processingConfig", { defaultValue: "Processing Configuration" })}
              subtitle={t("admin.processingConfigSubtitle", { defaultValue: "Configure workflows & tasks" })} />
            <SectionCard to="/admin/schemas" icon="table" accent="#6366f1"
              title={t("admin.schemas", { defaultValue: "Metadata Schemas" })}
              subtitle={t("admin.schemasSubtitle", { defaultValue: "Define validation rules" })} />
            <SectionCard to="/admin/system-config" icon="download" accent="#64748b"
              title={t("admin.systemConfig", { defaultValue: "System Configuration" })}
              subtitle={t("admin.systemConfigSubtitle", { defaultValue: "Export and import all configuration" })} />
            <SectionCard to="/admin/org-users" icon="users" accent="#4f46e5"
              title={t("admin.orgUsers", { defaultValue: "Organization Users" })}
              subtitle={t("admin.orgUsersSubtitle", { defaultValue: "Manage user roles" })} />
          </div>
        </section>

        {/* ── More ── */}
        <section className="adm-section">
          <div className="adm-section__head">
            <h2 className="adm-section__heading">
              {t("admin.more", { defaultValue: "More" })}
            </h2>
          </div>
          <div className="adm-cards-grid">
            <SectionCard to="/system-logs" icon="filelog" accent="#64748b"
              title={t("system.logs", { defaultValue: "System Logs" })}
              subtitle={t("system.empty.subtitle", { defaultValue: "Inspect activity & errors" })} />
            <SectionCard to="/notifications" icon="bell" accent="#f59e0b"
              title={t("user.notifications", { defaultValue: "Notifications" })}
              subtitle={t("user.allNotifications", { defaultValue: "Review admin messages" })} />
            <SectionCard to="/admin/audit-logs" icon="filelog" accent="#8b5cf6"
              title={t("audit.title", { defaultValue: "Audit Logs" })}
              subtitle={t("audit.subtitle", { defaultValue: "Track user actions" })} />
            <SectionCard to="/admin/archived-databases" icon="archive" accent="#0ea5e9"
              title={t("admin.archivedDatabases", { defaultValue: "Archived Databases" })}
              subtitle={t("admin.archivedSubtitle", { defaultValue: "Restore or remove databases" })} />
            <SectionCard to="/admin/definitions" icon="list" accent="#10b981"
              title={t("admin.definitions", { defaultValue: "Definitions" })}
              subtitle={t("admin.definitionsSubtitle", { defaultValue: "Manage lookup lists" })} />
            <SectionCard to="/admin/monitoring" icon="workflow" accent="#ef4444"
              title={t("monitoring.title", { defaultValue: "API Monitoring" })}
              subtitle={t("monitoring.hubSubtitle", { defaultValue: "Request rates, errors & latency" })} />
            <SectionCard to="/admin/license" icon="shield" accent="#4f46e5"
              title={t("license.status.title", { defaultValue: "License" })}
              subtitle={t("admin.licenseSubtitle", { defaultValue: "View status & activate" })} />
            <SectionCard
              to="/admin/update"
              icon="trendUp"
              accent="#f59e0b"
              title={
                <span style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                  {t("update.adminTile", { defaultValue: "System Updates" })}
                  {updateStatus?.updateAvailable && (
                    <span className="adm-badge-new">NEW</span>
                  )}
                </span>
              }
              subtitle={t("update.adminTileSubtitle", { defaultValue: "Check and apply software updates" })}
            />
          </div>
        </section>
      </div>
    </div>
  );
}
